/**
 * Arborescence repliable (04/11/2026) — motif « Tree View » de WAI-ARIA : `role="tree"`, `treeitem`, `group`,
 * `aria-expanded`, `aria-level`, `aria-posinset`, `aria-setsize`, tabulation itinérante (un seul arrêt de tabulation,
 * puis les flèches). La logique du clavier est dans `arbre-clavier.ts` (testée). Chaque nœud porte un état visuel
 * (fait, à faire, en attente, à télécharger, à venir) en couleur ET en texte, jamais la couleur seule (RGAA 3.1).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Check,
  ChevronRight,
  CircleDashed,
  Clock,
  Download,
  FileText,
  Folder,
  FolderOpen,
  GraduationCap,
  ListChecks,
  UserRound,
} from "lucide-react";
import { LIBELLES_ETAT, type EtatNoeud, type NoeudArbre } from "@/domaine/accueil-apprenant/arbre";
import { ancetres, aplatir, effetTouche } from "./arbre-clavier";
import { cx } from "./base";

export const STYLE_ETAT: Record<EtatNoeud, { pastille: string; icone: React.ReactNode }> = {
  fait: {
    pastille: "bg-valide-doux text-valide ring-valide/30",
    icone: <Check className="size-3" aria-hidden />,
  },
  a_faire: {
    pastille: "bg-attente-doux text-attente-encre ring-attente/60",
    icone: <span className="size-1.5 rounded-full bg-attente-encre" aria-hidden />,
  },
  en_attente: {
    pastille: "bg-papier-3 text-encre-2 ring-trait-fort",
    icone: <Clock className="size-3" aria-hidden />,
  },
  a_telecharger: {
    pastille: "bg-accent-doux text-accent-fort ring-accent/30",
    icone: <Download className="size-3" aria-hidden />,
  },
  a_venir: {
    pastille: "bg-carte text-encre-3 ring-trait-fort ring-dashed",
    icone: <CircleDashed className="size-3" aria-hidden />,
  },
};

export function PastilleEtat({
  etat,
  detail,
  compacte = false,
}: {
  etat: EtatNoeud;
  detail?: string;
  compacte?: boolean;
}) {
  const st = STYLE_ETAT[etat];
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold ring-1 ring-inset",
        st.pastille,
      )}
    >
      {st.icone}
      <span className={compacte ? "sr-only" : undefined}>{detail ?? LIBELLES_ETAT[etat]}</span>
    </span>
  );
}

function IconeGenre({ genre, ouvert }: { genre: NoeudArbre["genre"]; ouvert: boolean }) {
  const c = "size-4 shrink-0 text-encre-3";
  switch (genre) {
    case "formation":
      return <GraduationCap className={cx(c, "text-accent")} aria-hidden />;
    case "dossier":
    case "groupe":
    case "etape":
      return ouvert ? <FolderOpen className={c} aria-hidden /> : <Folder className={c} aria-hidden />;
    case "apprenant":
      return <UserRound className={c} aria-hidden />;
    case "formulaire":
      return <ListChecks className={c} aria-hidden />;
    case "supports":
      return <BookOpen className={c} aria-hidden />;
    default:
      return <FileText className={c} aria-hidden />;
  }
}

export function Arborescence({
  racines,
  libelle,
  ouvertsInitiaux = [],
  selection,
  activer,
  className,
}: {
  racines: readonly NoeudArbre[];
  libelle: string;
  ouvertsInitiaux?: readonly string[];
  selection?: string | null;
  activer?: (n: NoeudArbre) => void;
  className?: string;
}) {
  const [ouverts, setOuverts] = useState<Set<string>>(() => new Set(ouvertsInitiaux));
  const visibles = useMemo(() => aplatir(racines, ouverts), [racines, ouverts]);
  const [focus, setFocus] = useState<string | null>(null);
  const refs = useRef(new Map<string, HTMLLIElement>());
  const doitFocaliser = useRef(false);
  // Le nœud qui porte la tabulation : celui qui a le focus, sinon la sélection, sinon le premier.
  const actif = visibles.some((v) => v.noeud.id === focus)
    ? focus
    : visibles.some((v) => v.noeud.id === selection)
      ? selection!
      : (visibles[0]?.noeud.id ?? null);

  // Une sélection venue de l'extérieur ouvre le chemin jusqu'à elle.
  useEffect(() => {
    if (!selection) return;
    const chemin = ancetres(racines, selection);
    if (chemin.some((id) => !ouverts.has(id))) setOuverts((o) => new Set([...o, ...chemin]));
  }, [selection, racines]);

  useEffect(() => {
    if (doitFocaliser.current && actif) {
      refs.current.get(actif)?.focus();
      doitFocaliser.current = false;
    }
  }, [actif, visibles]);

  const basculer = (id: string) =>
    setOuverts((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Clic ou Entrée : une branche s'ouvre ou se ferme ; le nœud est aussi « activé » (sélection, défilement…).
  const cliquer = (noeud: NoeudArbre) => {
    if (noeud.enfants.length > 0) basculer(noeud.id);
    activer?.(noeud);
  };

  const surTouche = (e: React.KeyboardEvent, id: string) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const effet = effetTouche(visibles, id, e.key);
    if (!effet) return;
    e.preventDefault();
    e.stopPropagation();
    if (effet.ouvrir) setOuverts((o) => new Set([...o, ...effet.ouvrir!]));
    if (effet.fermer) setOuverts((o) => new Set([...o].filter((x) => x !== effet.fermer)));
    if (effet.focus) {
      doitFocaliser.current = true;
      setFocus(effet.focus);
    }
    if (effet.activer) {
      const v = visibles.find((x) => x.noeud.id === effet.activer)!;
      cliquer(v.noeud);
    }
  };

  const rendre = (liste: readonly NoeudArbre[], niveau: number): React.ReactNode =>
    liste.map((noeud, i) => {
      const aDesEnfants = noeud.enfants.length > 0;
      const ouvert = aDesEnfants && ouverts.has(noeud.id);
      const selectionne = selection === noeud.id;
      return (
        <li
          key={noeud.id}
          ref={(el) => {
            if (el) refs.current.set(noeud.id, el);
            else refs.current.delete(noeud.id);
          }}
          role="treeitem"
          aria-level={niveau}
          aria-posinset={i + 1}
          aria-setsize={liste.length}
          aria-expanded={aDesEnfants ? ouvert : undefined}
          aria-selected={selection !== undefined ? selectionne : undefined}
          aria-label={`${noeud.libelle}${noeud.etat ? ` — ${noeud.detail && noeud.detail !== LIBELLES_ETAT[noeud.etat] ? `${LIBELLES_ETAT[noeud.etat]}, ${noeud.detail}` : LIBELLES_ETAT[noeud.etat]}` : noeud.detail ? ` — ${noeud.detail}` : ""}`}
          tabIndex={actif === noeud.id ? 0 : -1}
          data-etat={noeud.etat ?? undefined}
          onKeyDown={(e) => surTouche(e, noeud.id)}
          onFocus={(e) => {
            if (e.target === e.currentTarget) setFocus(noeud.id);
          }}
          className="rounded-sm outline-none focus-visible:[&>div]:ring-[3px] focus-visible:[&>div]:ring-focus/60"
        >
          <div
            className={cx(
              "group flex min-h-9 cursor-pointer items-center gap-1.5 rounded-sm pr-1.5 text-sm transition-colors duration-150 select-none hover:bg-papier-3",
              selectionne ? "bg-accent-doux font-semibold text-accent-fort" : "text-encre",
            )}
            style={{ paddingLeft: `${(niveau - 1) * 14 + 4}px` }}
            onClick={() => {
              setFocus(noeud.id);
              cliquer(noeud);
            }}
          >
            <span className="grid size-5 shrink-0 place-items-center text-encre-3" aria-hidden>
              {aDesEnfants && (
                <ChevronRight
                  className={cx("size-4 transition-transform duration-150", ouvert && "rotate-90")}
                />
              )}
            </span>
            <IconeGenre genre={noeud.genre} ouvert={ouvert} />
            <span className="min-w-0 flex-1 truncate" title={noeud.libelle}>
              {noeud.libelle}
            </span>
            {noeud.etat && (
              <PastilleEtat
                etat={noeud.etat}
                detail={noeud.detail}
                compacte={niveau > 2 && noeud.genre !== "apprenant"}
              />
            )}
          </div>
          {ouvert && (
            <ul role="group" className="mt-0.5 space-y-0.5">
              {rendre(noeud.enfants, niveau + 1)}
            </ul>
          )}
        </li>
      );
    });

  return (
    <ul role="tree" aria-label={libelle} className={cx("space-y-0.5", className)}>
      {rendre(racines, 1)}
    </ul>
  );
}
